// src/pages/ProfileOrders.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ProfileOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get('/orders/my-orders');
        setOrders(res.data);
      } catch (err) {
        console.error('خطا در دریافت فاکتورها:', err);
        setError(err.response?.data?.message || 'خطا در بارگذاری فاکتورها');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-2xl text-gold-dark font-bold animate-pulse">در حال بارگذاری فاکتورها...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl text-red-600 font-bold mb-6">{error}</p>
          <Link to="/products" className="bg-gold hover:bg-gold-dark text-white font-bold py-4 px-10 rounded-2xl text-xl transition shadow-xl">
            ادامه خرید
          </Link>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-20 text-center">
        <div className="text-6xl mb-6">📦</div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4">هنوز فاکتوری ندارید</h2>
        <p className="text-xl text-gray-600 mb-10">با خرید اولین محصول، فاکتور شما اینجا نمایش داده می‌شود</p>
        <Link to="/products" className="bg-gold hover:bg-gold-dark text-white font-bold py-5 px-12 rounded-2xl text-2xl transition shadow-2xl transform hover:scale-105">
          شروع خرید
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gold-dark mb-4">فاکتورهای من</h1>
          <p className="text-xl text-gray-600">لیست خریدها و وضعیت پرداخت‌ها</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {orders.map(order => {
            const isCompleted = order.status === 'completed' || order.remaining_installments === 0;
            const hasInstallment = order.installment_count > 0;

            return (
              <div
                key={order.id}
                className={`bg-white rounded-3xl shadow-xl overflow-hidden border ${
                  isCompleted ? 'border-green-300' : 'border-gray-200'
                } hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}
              >
                <div className={`py-6 px-8 text-white ${
                  isCompleted ? 'bg-green-600' : 'bg-gradient-to-r from-gold-light to-gold'
                }`}>
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold">فاکتور #{order.order_number}</h2>
                    <span className={`px-5 py-2 rounded-full text-sm font-bold ${
                      isCompleted ? 'bg-white text-green-800' : 
                      order.status === 'pre_paid' ? 'bg-green-700' : 'bg-orange-600'
                    }`}>
                      {isCompleted ? 'تسویه کامل ✓' : order.display_status || 'در حال پرداخت'}
                    </span>
                  </div>
                  <p className="mt-2 opacity-90">
                    تاریخ: {formatDate(order.created_at)}
                  </p>
                </div>

                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-gray-50 rounded-xl p-5">
                      <p className="text-gray-600 text-sm">پیش‌پرداخت</p>
                      <p className="text-2xl font-bold text-gold-dark mt-1">
                        {toToman(order.total_pre_payment)} تومان
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-5">
                      <p className="text-gray-600 text-sm">بدهی باقی‌مانده</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">
                        {Number(order.total_gold_debt).toFixed(3)} گرم
                      </p>
                    </div>
                  </div>

                  {hasInstallment && (
                    <div className={`rounded-xl p-5 text-center ${
                      order.remaining_installments === 0 ? 'bg-green-50' : 'bg-indigo-50'
                    }`}>
                      <p className={`font-medium ${
                        order.remaining_installments === 0 ? 'text-green-700' : 'text-indigo-700'
                      }`}>
                        {order.remaining_installments === 0 
                          ? 'همه اقساط پرداخت شده ✓' 
                          : `${order.remaining_installments} قسط باقی‌مانده`}
                      </p>
                    </div>
                  )}

                  <Link
                    to={`/invoice/${order.id}`}
                    className="block w-full bg-gold hover:bg-gold-dark text-white font-bold py-5 px-8 rounded-2xl text-xl text-center transition shadow-xl transform hover:scale-105 mt-6"
                  >
                    مشاهده جزئیات و اقساط
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// تابع کمکی برای تومان
const toToman = (amount) => {
  return Number(amount || 0).toLocaleString('fa-IR');
};

export default ProfileOrders;